import { PatientId, WithPatientId } from '../surgicalEvent.slice';
import { doctorBffApi, InvalidateArr } from '@/modules';
import {
  GroupedMedicalDocuments,
  insuranceDocumentTypes,
  MedicalDocument,
  MedicalDocumentType,
  prescriptionDocumentTypes,
  uploadableDocumentTypes
} from './medicalDocument.model';
import { MedicalDocumentService } from './medicalDocument.service';
import { setUploadProgress } from '@/modules/ui';
import { wrapError } from '../../error';
import { constructProtocolCacheKey, ProtocolName } from '@/modules/protocol';
import { ID } from '@ianesthesie-kit/services';

export const medicalDocumentApi = doctorBffApi.injectEndpoints({
  endpoints: (builder) => ({
    medicalDocumentsByType: builder.query<
      GroupedMedicalDocuments,
      WithPatientId<{ types: MedicalDocumentType[] }>
    >({
      queryFn: ({ patientId, types }) =>
        MedicalDocumentService.getInstance(patientId)
          .fetchGrouped<MedicalDocumentType, true>({
            types
          })
          .catch(wrapError),
      providesTags: (result, error, { patientId }) => [
        { type: 'MedicalDocument', id: `${patientId}_byType` }
      ]
    }),
    medicalDocuments: builder.query<GroupedMedicalDocuments, PatientId>({
      queryFn: ({ patientId }) =>
        MedicalDocumentService.getInstance(patientId)
          .fetchGrouped<MedicalDocumentType, true>({
            types: [
              ...uploadableDocumentTypes,
              MedicalDocumentType.CONSENT_FORM
            ]
          })
          .catch(wrapError),
      providesTags: (result, error, { patientId }) => [
        { type: 'MedicalDocument', id: `${patientId}_medical` }
      ]
    }),

    prescriptionDocuments: builder.query<GroupedMedicalDocuments, PatientId>({
      queryFn: ({ patientId }) =>
        MedicalDocumentService.getInstance(patientId)
          .fetchGrouped<MedicalDocumentType, true>({
            types: prescriptionDocumentTypes
          })
          .catch(wrapError),
      providesTags: (result, error, { patientId }) => [
        { type: 'MedicalDocument', id: patientId },
        { type: 'MedicalDocument', id: `${patientId}_prescription` }
      ]
    }),

    insuranceDocuments: builder.query<MedicalDocument[], PatientId>({
      queryFn: ({ patientId }) =>
        MedicalDocumentService.getInstance(patientId)
          .fetch({
            types: insuranceDocumentTypes
          })
          .catch(wrapError),
      providesTags: (result, error, { patientId }) => [
        { type: 'MedicalDocument', id: patientId },
        { type: 'MedicalDocument', id: `${patientId}_insurance` }
      ]
    }),

    operatorInvoiceDocuments: builder.query<MedicalDocument[], PatientId>({
      queryFn: ({ patientId }) =>
        MedicalDocumentService.getInstance(patientId)
          .fetch({
            types: [MedicalDocumentType.OPERATOR_INVOICE]
          })
          .catch(wrapError),
      providesTags: (result, error, { patientId }) => [
        { type: 'MedicalDocument', id: patientId },
        { type: 'MedicalDocument', id: `${patientId}_invoice` }
      ]
    }),

    upsertMedicalDocument: builder.mutation<
      MedicalDocument[],
      WithPatientId<{
        document: Partial<MedicalDocument>;
        newFiles?: File[];
      }>
    >({
      queryFn: ({ patientId, document, newFiles }, { dispatch }) => {
        const onProgress = ({ loaded, total }: ProgressEvent) => {
          dispatch(setUploadProgress(loaded / total));
        };

        return MedicalDocumentService.getInstance(patientId)
          .upload(document, newFiles, onProgress)
          .catch(wrapError)
          .finally(() => {
            dispatch(setUploadProgress(undefined));
          });
      },
      invalidatesTags: (result, error, { patientId, document }) =>
        getMedicalDocumentInvalidateTags(result, patientId, document?.type)
    }),

    deleteMedicalDocument: builder.mutation<
      MedicalDocument[],
      WithPatientId<{
        document: MedicalDocument;
      }>
    >({
      queryFn: ({ patientId, document }) =>
        MedicalDocumentService.getInstance(patientId)
          .delete(document)
          .catch(wrapError),
      invalidatesTags: (result, error, { patientId, document }) =>
        getMedicalDocumentInvalidateTags(result, patientId, document?.type)
    }),

    deleteMedicalDocumentMedium: builder.mutation<
      MedicalDocument[],
      WithPatientId<{
        document: MedicalDocument;
        mediumId: ID;
      }>
    >({
      queryFn: ({ patientId, document, mediumId }) =>
        MedicalDocumentService.getInstance(patientId)
          .deleteMedium(document, mediumId)
          .catch(wrapError),
      invalidatesTags: (result, error, { patientId, document }) =>
        getMedicalDocumentInvalidateTags(result, patientId, document?.type)
    }),

    triageMedicalDocument: builder.mutation<
      void,
      WithPatientId<{
        document: MedicalDocument;
      }>
    >({
      queryFn: ({ patientId, document }) =>
        MedicalDocumentService.getInstance(patientId)
          .triage(document)
          .then(() => ({ data: {} as any }))
          .catch(wrapError),
      invalidatesTags: (result, error, { patientId }) =>
        error
          ? []
          : [
              { type: 'MedicalDocument', id: `${patientId}_medical` },
              { type: 'MedicalDocument', id: `${patientId}_byType` }
            ]
    })
  }),
  overrideExisting: false
});

function getMedicalDocumentInvalidateTags(
  result: MedicalDocument[] | undefined,
  patientId: string,
  documentType: MedicalDocumentType | undefined
): InvalidateArr {
  const arr: InvalidateArr = [
    { type: 'LifenAvailableDocumentTypes', id: patientId }
  ];
  if (!result) {
    return arr;
  }
  const t = documentType;
  if (t) {
    if (t === MedicalDocumentType.OPERATOR_INVOICE) {
      arr.push({ type: 'MedicalDocument', id: `${patientId}_invoice` });
    }
    if (
      t === MedicalDocumentType.CONSENT_FORM ||
      uploadableDocumentTypes.includes(t)
    ) {
      arr.push({ type: 'MedicalDocument', id: `${patientId}_medical` });
    }
    if (insuranceDocumentTypes.includes(t)) {
      arr.push({ type: 'MedicalDocument', id: `${patientId}_insurance` });
    }
    if (prescriptionDocumentTypes.includes(t)) {
      arr.push({ type: 'MedicalDocument', id: `${patientId}_prescription` });
    }

    if (!arr.length) {
      arr.push({ type: 'MedicalDocument', id: patientId });
    }
    arr.push({ type: 'MedicalDocument', id: `${patientId}_byType` });
  } else {
    arr.push({ type: 'MedicalDocument', id: patientId });
  }

  if (t === MedicalDocumentType.CARDIAC_TEST) {
    arr.push(
      { type: 'ProtocolStatuses', id: patientId },
      {
        type: 'Protocol',
        id: constructProtocolCacheKey({
          patientId,
          protocol: ProtocolName.CARDIAC_STRESS_TEST
        })
      }
    );
  }

  return arr;
}

export const {
  useMedicalDocumentsQuery,
  useUpsertMedicalDocumentMutation,
  useDeleteMedicalDocumentMutation,
  useDeleteMedicalDocumentMediumMutation,
  useTriageMedicalDocumentMutation,
  usePrescriptionDocumentsQuery,
  useInsuranceDocumentsQuery,
  useOperatorInvoiceDocumentsQuery,
  useMedicalDocumentsByTypeQuery
} = medicalDocumentApi;
